/**
 * useBlockchainData
 *
 * Handles blockchain data processing and creates transaction visuals.
 */

import { ref } from 'vue';
import type { BlockchainState } from '@/stores/blockchainState';
import type { MiniblockMessage, MiniblockUpdate, BlockMessage, BlockUpdate, InitialStateMessage, WebSocketClient } from '@/core/WebSocketClient';
import type { VisualizationRenderers } from '@/composables/useVisualization';
import { TX_TRAVEL_TIME } from '@/config/visualization.config';
import { MinHeap } from '@/utils/MinHeap';
import { updateOliLabels, updateOliLabelsFull } from '@/data/projects';

export interface PendingMiniblock {
  blockNumber: number;
  index: number;
  txCount: number;
  gasUsed: string;
  timestamp: number;
}

export interface BlockVerification {
  blockNumber: number;
  expected: number;  // Expected miniblock count from block
  received: number;  // Actual miniblocks received
  status: 'pending' | 'verified' | 'warning'; // Verification status
  detectedAt?: number; // Timestamp when warning was detected (for countdown)
}

export function useBlockchainData(
  state: BlockchainState,
  renderers: VisualizationRenderers
) {
  let initialBlockReceived = false;
  let lastBlockTime = Date.now();
  let firstBlockNumber: number | null = null; // Track first block to skip verification (we connect mid-block)

  // MinHeap to store miniblocks in sorted order for efficient retrieval
  const pendingMiniBlocks = new MinHeap<PendingMiniblock>((a, b) => {
    if (a.blockNumber !== b.blockNumber) {
      return a.blockNumber - b.blockNumber;
    }
    return a.index - b.index;
  });

  // Track when we see transitions between blocks for blocks/sec calculation
  let lastSeenBlockNumber = -1;
  let firstBlockTransitionSkipped = false; // Skip first transition (no valid reference point)
  let lastBlockTimestamp = 0; // On-chain timestamp of last block (for accurate BPS)
  let lastFrontendBlockTime = 0; // Frontend wall-clock time of last block (for ingress rate)

  // Pause buffer: stores miniblocks received while paused (last 10 blocks only)
  const pauseBuffer: PendingMiniblock[] = [];
  const PAUSE_BUFFER_BLOCK_LIMIT = 10;

  // Auto-catchup: process miniblocks in small batches to avoid UI freezes
  // Lower threshold + small batches = smoother catchup without frame drops
  const AUTO_CATCHUP_THRESHOLD = 200; // Start catching up earlier
  const CATCHUP_BATCH_SIZE = 50; // Process 50 at a time (avoids freezing)

  // Miniblock verification: track received miniblocks per block
  const miniblockCounts = new Map<number, number>(); // blockNumber -> count of received miniblocks
  const blockVerifications = new Map<number, BlockVerification>(); // blockNumber -> verification result
  const MAX_VERIFICATION_HISTORY = 100; // Keep last 100 blocks for verification

  // Sync status tracking
  const pageLoadTime = Date.now();
  const SYNC_BLEND_PERIOD_MS = 60000; // 60 seconds for historical data blending
  const syncCountdown = ref(0); // Countdown in seconds (starts at 0, set by updateSyncStatus)

  const updateSyncStatus = () => {
    const now = Date.now();
    const timeSinceLoad = now - pageLoadTime;

    // Check if there are circles that actually need syncing (from initial_state)
    const hasCirclesNeedingSync = renderers.circle?.hasCirclesNeedingSync() ?? false;

    // Only sync when we have saved circles that need historical data
    // Block verification warnings are informational but shouldn't trigger sync mode
    if (timeSinceLoad < SYNC_BLEND_PERIOD_MS && hasCirclesNeedingSync) {
      const remainingMs = SYNC_BLEND_PERIOD_MS - timeSinceLoad;
      syncCountdown.value = Math.max(0, Math.ceil(remainingMs / 1000));
      state.isSyncing.value = true;
      return;
    }

    // Not syncing - countdown is 0
    syncCountdown.value = 0;
    state.isSyncing.value = false;
  };

  // Update sync status every second (skip when tab hidden)
  // Also call immediately to set initial state
  updateSyncStatus();
  const syncIntervalId = setInterval(() => {
    if (document.hidden) return; // Skip when tab is hidden to save CPU
    updateSyncStatus();
  }, 1000);

  // Rolling 60-second window for local stats calculation
  // Each entry represents data from a single miniblock
  interface RollingWindowEntry {
    timestamp: number;
    txCount: number;
    gasUsed: number;
    addressActivity: Map<string, { txc: number; gas: number }>;
  }
  const rollingWindow: RollingWindowEntry[] = [];
  const ROLLING_WINDOW_MS = 60000; // 60 seconds

  // Throttle stats UI updates to once per second (data still accumulated on every miniblock)
  let lastStatsUpdateTime = 0;
  const STATS_UPDATE_INTERVAL_MS = 1000; // 1 second

  // Track when first miniblock arrives (for accurate stats window calculation)
  let firstMiniblockTime: number | null = null;

  // Backend stats for blending (received from initial_state)
  // These are used to smoothly transition from backend to local calculation over 60 seconds
  let backendTps = 0;
  let backendGps = 0;
  let backendMbps = 0;

  // WebSocket client reference for requesting fresh state on resume
  let wsClientRef: WebSocketClient | null = null;

  /**
   * Add miniblock data to rolling window (called on every miniblock)
   * Only recalculates and updates UI stats once per second max
   */
  const updateRollingWindow = (txCount: number, gasUsed: number, addrs: Array<{ addr: string; txc: number; gas: number }>) => {
    const now = Date.now();

    // Track when first miniblock arrives (for accurate window duration)
    if (firstMiniblockTime === null) {
      firstMiniblockTime = now;
    }

    // Create address activity map for this miniblock
    const addressActivity = new Map<string, { txc: number; gas: number }>();
    for (const addr of addrs) {
      addressActivity.set(addr.addr, { txc: addr.txc, gas: addr.gas });
    }

    // Add new entry (always accumulate data)
    rollingWindow.push({
      timestamp: now,
      txCount,
      gasUsed,
      addressActivity
    });

    // Remove entries older than 60 seconds (efficient shift from front)
    const cutoff = now - ROLLING_WINDOW_MS;
    while (rollingWindow.length > 0 && rollingWindow[0].timestamp < cutoff) {
      rollingWindow.shift();
    }

    // Throttle UI updates to once per second
    if (now - lastStatsUpdateTime < STATS_UPDATE_INTERVAL_MS) {
      return; // Skip UI update, data is already accumulated
    }
    lastStatsUpdateTime = now;

    // Calculate totals from window
    let totalTx = 0;
    let totalGas = 0;
    const totalMiniblocks = rollingWindow.length;

    // Aggregate address stats across window
    const aggregatedAddresses = new Map<string, { txc: number; gas: number }>();

    for (const entry of rollingWindow) {
      totalTx += entry.txCount;
      totalGas += entry.gasUsed;

      // Aggregate address activity
      for (const [addr, activity] of entry.addressActivity) {
        const existing = aggregatedAddresses.get(addr);
        if (existing) {
          existing.txc += activity.txc;
          existing.gas += activity.gas;
        } else {
          aggregatedAddresses.set(addr, { txc: activity.txc, gas: activity.gas });
        }
      }
    }

    // Always update circle rankings from aggregated address data
    // Per-address stats are for relative comparison/ranking, so local data is fine
    if (renderers.circle) {
      renderers.circle.updateFromLocalStats(aggregatedAddresses);
    }

    // Calculate window duration (how long we've been collecting data)
    const windowDuration = now - firstMiniblockTime!;
    const windowSeconds = Math.max(0.001, windowDuration / 1000); // Avoid division by zero

    // Calculate local rates over the actual window duration
    // This gives accurate rates as long as we have enough data
    const localTps = totalTx / windowSeconds;
    const localGps = totalGas / windowSeconds;
    const localMbps = totalMiniblocks / windowSeconds;

    if (windowSeconds < 60 && (backendTps > 0 || backendGps > 0 || backendMbps > 0)) {
      // Blend backend + local with SQUARED weight to suppress early volatility
      // At 0s: 100% backend, 0% local
      // At 30s: 75% backend, 25% local (30/60)^2 = 0.25
      // At 60s: 0% backend, 100% local
      // Squaring means we trust backend more in the early phase
      const linearWeight = windowSeconds / 60;
      const localWeight = linearWeight * linearWeight; // Squared for smoother transition
      const backendWeight = 1 - localWeight;

      state.tps.value = (backendTps * backendWeight) + (localTps * localWeight);
      state.gasPerSec.value = (backendGps * backendWeight) + (localGps * localWeight);
      state.miniblocksPerSec.value = (backendMbps * backendWeight) + (localMbps * localWeight);
    } else if (windowSeconds < 60) {
      // No backend stats - just use backend values (set by handleInitialState)
      // Don't update from local yet to avoid wild swings
    } else {
      // Full 60s window - use pure local calculation
      state.tps.value = localTps;
      state.gasPerSec.value = localGps;
      state.miniblocksPerSec.value = localMbps;
    }

    // Update sparkline history
    state.updateHistory();
  };

  /**
   * Handle miniblock message from WebSocket
   */
  const handleMiniblock = (data: MiniblockMessage) => {
    const blockNumber = data.block_number;
    const index = data.index;
    const txCount = data.transactions?.length || data.tx_count || 0;
    const gasUsed = data.gas_used || '0';

    // Initialize with first block received
    if (!initialBlockReceived) {
      state.setCurrentBlock(blockNumber);
      initialBlockReceived = true;
    }

    // Update current block if newer
    if (blockNumber > state.currentBlock.value) {
      state.setCurrentBlock(blockNumber);
    }

    // Calculate blocks/sec when we see a new block number
    if (blockNumber !== lastSeenBlockNumber && lastSeenBlockNumber !== -1) {
      const now = Date.now();
      if (firstBlockTransitionSkipped) {
        // Only record valid block times (skip the first one - no valid reference point)
        const timeSinceLastBlock = now - lastBlockTime;
        state.addBlockTime(timeSinceLastBlock);
      } else {
        firstBlockTransitionSkipped = true;
      }
      lastBlockTime = now;
    }
    lastSeenBlockNumber = blockNumber;

    // Create pending miniblock
    const pendingMiniblock: PendingMiniblock = {
      blockNumber,
      index,
      txCount,
      gasUsed,
      timestamp: Date.now()
    };

    // If paused OR tab hidden, add to buffer instead of processing
    if (state.isPaused.value || document.hidden) {
      pauseBuffer.push(pendingMiniblock);

      // Efficient trim: remove entries older than 60 seconds
      // Use shift() instead of filter() - O(k) where k = items to remove
      const cutoffTime = Date.now() - 60000; // 60 seconds ago
      while (pauseBuffer.length > 0 && pauseBuffer[0].timestamp < cutoffTime) {
        pauseBuffer.shift();
      }

      // Hard cap as safety (prevents runaway growth)
      if (pauseBuffer.length > 2000) {
        pauseBuffer.splice(0, pauseBuffer.length - 2000);
      }

      return; // Don't process while paused/hidden
    }

    // Add miniblock to pending heap for bar graph (always, even if empty)
    pendingMiniBlocks.push(pendingMiniblock);

    // Create transaction visuals only if there are transactions
    if (renderers.transaction && txCount > 0) {
      // Create/update circles for transaction addresses and collect circle positions
      if (renderers.circle && data.transactions && Array.isArray(data.transactions)) {
        const circlePositions: Array<{ x: number; y: number; radius: number }> = [];

        // Build efficient gas lookup map from receipts (O(n) - one pass)
        const gasLookup = new Map<string, number>();
        if (data.receipts && Array.isArray(data.receipts)) {
          for (const receipt of data.receipts) {
            if (receipt.transactionHash && receipt.gasUsed) {
              const gasUsed = typeof receipt.gasUsed === 'string'
                ? parseInt(receipt.gasUsed, 16) // Parse hex string
                : receipt.gasUsed;
              gasLookup.set(receipt.transactionHash.toLowerCase(), gasUsed);
            }
          }
        }

        for (const tx of data.transactions) {
          // Create circle for "to" address (destination) and pass gas used
          if (tx.to) {
            // Look up gas from receipts (O(1) lookup)
            const gasUsed = gasLookup.get(tx.hash?.toLowerCase()) || 0;
            const circlePos = renderers.circle.getOrCreateCircleForAddress(tx.to, gasUsed);
            circlePositions.push(circlePos);
          }
        }

        // Apply transaction multiplier for dev/performance testing
        const multiplier = state.txMultiplier.value;
        let multipliedPositions: Array<{ x: number; y: number; radius: number }>;

        if (multiplier === 1) {
          // Production mode: use correct circles for each transaction
          multipliedPositions = circlePositions;
        } else {
          // Testing mode (multiplier > 1):
          // First batch uses correct circles (production behavior)
          // Extra transactions use random circles for visual variety
          const allCircles = renderers.circle.getActiveCircles();

          if (allCircles.length === 0) {
            // No circles available, just duplicate originals as fallback
            multipliedPositions = new Array(circlePositions.length * multiplier);
            let idx = 0;
            for (let i = 0; i < multiplier; i++) {
              for (const pos of circlePositions) {
                multipliedPositions[idx++] = pos;
              }
            }
          } else {
            // Pre-allocate array
            multipliedPositions = new Array(circlePositions.length * multiplier);
            let idx = 0;

            // First iteration: use actual transaction circles (production behavior)
            for (const pos of circlePositions) {
              multipliedPositions[idx++] = pos;
            }

            // Remaining iterations: pick random circles for visual variety
            const extraTxCount = circlePositions.length * (multiplier - 1);
            for (let i = 0; i < extraTxCount; i++) {
              const randomCircle = allCircles[Math.floor(Math.random() * allCircles.length)];
              multipliedPositions[idx++] = randomCircle;
            }
          }
        }

        // Now create transaction particles from the specific circle positions
        renderers.transaction.createTransactions(blockNumber, index, multipliedPositions);
      }
    }
  };

  /**
   * Reset data state (useful when pausing)
   */
  const resetDataState = () => {
    // Nothing to reset for now
  };

  /**
   * Pause data processing
   */
  const pause = () => {
    // console.log(`⏸️ pause() called, current isPaused=${state.isPaused.value}`);
    state.isPaused.value = true;
    // console.log(`⏸️ pause() set isPaused=${state.isPaused.value}`);

    // Transfer pending miniblocks to pause buffer
    while (!pendingMiniBlocks.isEmpty()) {
      const miniblock = pendingMiniBlocks.pop();
      if (miniblock) {
        pauseBuffer.push(miniblock);
      }
    }

    // console.log(`⏸️ Data processing paused (${pauseBuffer.length} miniblocks buffered)`);
  };

  /**
   * Resume data processing and flush buffered miniblocks
   */
  const resume = () => {
    // console.log(`▶️ resume() called, current isPaused=${state.isPaused.value}`);
    state.isPaused.value = false;
    // console.log(`▶️ resume() set isPaused=${state.isPaused.value}`);

    // Request fresh stats from backend (pause may have caused drift)
    // This triggers handleInitialState which sets grace period
    if (wsClientRef) {
      wsClientRef.requestFreshState();
    }

    // Sort buffered miniblocks by block number and index
    pauseBuffer.sort((a, b) => {
      if (a.blockNumber !== b.blockNumber) {
        return a.blockNumber - b.blockNumber;
      }
      return a.index - b.index;
    });

    // console.log(`▶️ Resuming with ${pauseBuffer.length} buffered miniblocks`);

    // Instantly create all buffered bars at once (no animation, no delay)
    if (renderers.barGraph && pauseBuffer.length > 0) {
      renderers.barGraph.createInstantBars(pauseBuffer);
    }

    // Clear buffer
    pauseBuffer.length = 0;
  };

  /**
   * Set WebSocket client reference (for requesting fresh state on resume)
   */
  const setWsClient = (client: WebSocketClient) => {
    wsClientRef = client;
  };

  // Debug: track queue size over time
  let lastQueueLogTime = 0;
  const QUEUE_LOG_INTERVAL = 2000; // Log every 2 seconds

  /**
   * Check if we're too far behind and need incremental catchup
   * Instead of creating 500+ bars at once (which freezes UI), process in small batches
   */
  const checkAutoCatchup = () => {
    const queueSize = pendingMiniBlocks.length;
    const now = Date.now();

    if (queueSize > AUTO_CATCHUP_THRESHOLD) {
      // Process a batch, not everything at once
      // Target: keep queue around threshold/2 to avoid constant catchup
      const excessCount = queueSize - Math.floor(AUTO_CATCHUP_THRESHOLD / 2);
      const batchSize = Math.min(CATCHUP_BATCH_SIZE, excessCount);

      if (batchSize <= 0) return;

      // Pop from heap (already sorted by block number and index)
      const batch: PendingMiniblock[] = [];
      for (let i = 0; i < batchSize && !pendingMiniBlocks.isEmpty(); i++) {
        const mb = pendingMiniBlocks.pop();
        if (mb) batch.push(mb);
      }

      // Create bars for this batch
      if (renderers.barGraph && batch.length > 0) {
        renderers.barGraph.createInstantBars(batch);
      }
    }
  };

  /**
   * Initialize timers on mount
   */
  const initializeTimers = () => {
    state.initializeTimers();
    lastBlockTime = Date.now();
  };

  /**
   * Get and remove the next miniblock with lowest block number + index
   * Used by BarGraphRenderer to create bars in order
   * Miniblocks are only returned after TX_TRAVEL_TIME has elapsed (to align with transactions reaching sequencer)
   *
   * O(1) peek + O(log n) pop using MinHeap - much faster than O(n) linear search
   */
  const getNextMiniblock = (): PendingMiniblock | null => {
    if (pendingMiniBlocks.isEmpty()) {
      return null;
    }

    const now = Date.now();
    const next = pendingMiniBlocks.peek();

    // Check if the next miniblock (lowest block/index) has waited long enough
    if (!next || (now - next.timestamp) < TX_TRAVEL_TIME) {
      return null; // Not ready yet
    }

    // Remove and return the miniblock
    return pendingMiniBlocks.pop();
  };

  /**
   * Handle lightweight miniblock update (NEW - 97-99% bandwidth reduction!)
   * Replaces handleMiniblock with pre-aggregated address data from backend
   */
  const handleMiniblockUpdate = (data: MiniblockUpdate) => {
    const blockNumber = data.bn;
    const index = data.idx;
    const txCount = data.totalTx;
    const gasUsed = data.totalGas.toString();

    // Update backend BPS from every miniblock message (real-time from backend)
    if (data.bps !== undefined) {
      state.backendBpsValue.value = data.bps;
    }

    // Update upstream BPS (raw arrival rate from MegaETH WebSocket)
    if (data.ubps !== undefined) {
      state.upstreamBps.value = data.ubps;
    }

    // Update rolling window with this miniblock's data (calculates TPS/GPS/MBPS locally)
    updateRollingWindow(txCount, data.totalGas, data.addrs);

    // Track miniblock count for verification
    const currentCount = miniblockCounts.get(blockNumber) || 0;
    miniblockCounts.set(blockNumber, currentCount + 1);

    // Mark block as pending verification if not already verified
    if (!blockVerifications.has(blockNumber)) {
      blockVerifications.set(blockNumber, {
        blockNumber,
        expected: -1, // Unknown until block_update arrives
        received: currentCount + 1,
        status: 'pending'
      });
    } else {
      // Update received count for pending blocks
      const verification = blockVerifications.get(blockNumber)!;
      if (verification.status === 'pending') {
        verification.received = currentCount + 1;
      }
    }

    // Initialize with first block received
    if (!initialBlockReceived) {
      state.setCurrentBlock(blockNumber);
      initialBlockReceived = true;
      lastBlockTime = Date.now(); // Initialize with frontend time for ingress measurement
    }

    // Update current block if newer
    if (blockNumber > state.currentBlock.value) {
      state.setCurrentBlock(blockNumber);
    }

    // Calculate blocks/sec when we see a new block number
    // Track both on-chain BPS and frontend ingress BPS
    if (blockNumber !== lastSeenBlockNumber && lastSeenBlockNumber !== -1) {
      const now = Date.now();

      if (firstBlockTransitionSkipped) {
        // On-chain BPS: uses blockchain timestamps (actual block rate)
        if (lastBlockTimestamp > 0) {
          const blockTimeDelta = data.ts - lastBlockTimestamp;
          if (blockTimeDelta > 0 && blockTimeDelta < 10000) {
            state.addBlockTime(blockTimeDelta);
          }
        }

        // Frontend BPS: uses Date.now() (shows network + processing lag)
        if (lastFrontendBlockTime > 0) {
          const frontendTimeDelta = now - lastFrontendBlockTime;
          if (frontendTimeDelta > 0 && frontendTimeDelta < 10000) {
            state.addFrontendBlockTime(frontendTimeDelta);
          }
        }
      } else {
        firstBlockTransitionSkipped = true;
      }

      lastBlockTimestamp = data.ts;
      lastFrontendBlockTime = now;
    }
    lastSeenBlockNumber = blockNumber;

    // Create pending miniblock for bar graph
    const pendingMiniblock: PendingMiniblock = {
      blockNumber,
      index,
      txCount,
      gasUsed,
      timestamp: Date.now()
    };

    // If paused, add to buffer
    if (state.isPaused.value) {
      pauseBuffer.push(pendingMiniblock);

      const oldestBlockToKeep = blockNumber - PAUSE_BUFFER_BLOCK_LIMIT;
      if (pauseBuffer.length > 0 && pauseBuffer[0].blockNumber < oldestBlockToKeep) {
        const filtered = pauseBuffer.filter(mb => mb.blockNumber >= oldestBlockToKeep);
        pauseBuffer.length = 0;
        pauseBuffer.push(...filtered);
      }

      return; // Don't process while paused
    }

    // Add to pending heap for bar graph
    pendingMiniBlocks.push(pendingMiniblock);

    // Check if we're too far behind and need instant catchup
    checkAutoCatchup();

    // Create transaction visuals from address activity (already aggregated by backend!)
    if (renderers.transaction && renderers.circle && data.addrs.length > 0) {
      const circlePositions: Array<{ x: number; y: number; radius: number }> = [];

      // Process each address with activity
      for (const addrActivity of data.addrs) {
        // Update/create circle and get position (backend already extracted gas!)
        const circlePos = renderers.circle.getOrCreateCircleForAddress(
          addrActivity.addr,
          addrActivity.gas
        );

        // Repeat position based on transaction count (for visual effect)
        for (let i = 0; i < addrActivity.txc; i++) {
          circlePositions.push(circlePos);
        }
      }

      // Apply transaction multiplier for dev/performance testing
      const multiplier = state.txMultiplier.value;
      let multipliedPositions = circlePositions;

      if (multiplier > 1) {
        const allCircles = renderers.circle.getActiveCircles();
        if (allCircles.length > 0) {
          multipliedPositions = new Array(circlePositions.length * multiplier);
          let idx = 0;
          for (let i = 0; i < multiplier; i++) {
            if (i === 0) {
              for (const pos of circlePositions) {
                multipliedPositions[idx++] = pos;
              }
            } else {
              for (let j = 0; j < circlePositions.length; j++) {
                const randomCircle = allCircles[Math.floor(Math.random() * allCircles.length)];
                multipliedPositions[idx++] = randomCircle;
              }
            }
          }
        } else {
          multipliedPositions = new Array(circlePositions.length * multiplier);
          let idx = 0;
          for (let i = 0; i < multiplier; i++) {
            for (const pos of circlePositions) {
              multipliedPositions[idx++] = pos;
            }
          }
        }
      }

      // Create transaction animations
      renderers.transaction.createTransactions(blockNumber, index, multipliedPositions);
    }
  };

  // NOTE: stats_update removed - frontend now calculates stats locally from miniblock_update
  // See updateRollingWindow() for local stats calculation

  /**
   * Handle initial state message from WebSocket
   * Pre-populates circle rankings with historical data from last 60 seconds
   */
  const handleInitialState = (data: InitialStateMessage) => {

    // Update OLI labels from backend (address -> projectName mapping)
    if (data.labels) {
      updateOliLabels(data.labels);
    }
    // Update full OLI labels (for modal display)
    if (data.labelsFull) {
      updateOliLabelsFull(data.labelsFull);
    }

    // Migrate existing circles to new keys now that OLI labels are loaded
    // This prevents duplicate circles (raw address vs project key)
    if (renderers.circle && (data.labels || data.labelsFull)) {
      renderers.circle.migrateCircleKeys();
    }

    // Reset rolling window (will rebuild from new miniblocks)
    rollingWindow.length = 0;
    firstMiniblockTime = null;

    // Store backend stats for blending
    // These will be blended with local calculations over 60 seconds
    backendTps = data.globalTps ?? 0;
    backendGps = data.globalGps ?? 0;
    backendMbps = data.globalMbps ?? 0;

    // Set global stats from backend (immediate display)
    if (data.globalBps !== undefined && data.globalBps > 0) {
      state.setBackendBps(data.globalBps); // Also resets block times buffer
    }
    if (data.globalTps !== undefined) {
      state.tps.value = data.globalTps;
    }
    if (data.globalGps !== undefined) {
      state.gasPerSec.value = data.globalGps;
    }
    if (data.globalMbps !== undefined) {
      state.miniblocksPerSec.value = data.globalMbps;
    }

    if (renderers.circle) {
      // Pass initial state to CircleRenderer to spawn circles at correct sizes
      renderers.circle.setInitialState(data.projects);
    }
  };

  /**
   * Handle block update for miniblock verification
   * Compares expected vs received miniblock counts
   */
  const handleBlockUpdate = (data: BlockUpdate) => {
    const blockNumber = data.bn;
    const expectedCount = data.mbCount;
    const receivedCount = miniblockCounts.get(blockNumber) || 0;

    // Track first block number - we connected mid-block so it will always be incomplete
    if (firstBlockNumber === null) {
      firstBlockNumber = blockNumber;
    }

    // Skip verification for the first block (we connected mid-block)
    const isFirstBlock = blockNumber === firstBlockNumber;
    const status: 'verified' | 'warning' = (isFirstBlock || receivedCount >= expectedCount) ? 'verified' : 'warning';

    // Create/update verification result
    const verification: BlockVerification = {
      blockNumber,
      expected: expectedCount,
      received: receivedCount,
      status,
      detectedAt: status === 'warning' ? Date.now() : undefined
    };

    blockVerifications.set(blockNumber, verification);


    // Update bar graph label to show verification status
    if (renderers.barGraph) {
      renderers.barGraph.updateBlockVerification(blockNumber);
    }

    // Cleanup old verification data (keep last 100 blocks)
    if (blockVerifications.size > MAX_VERIFICATION_HISTORY) {
      const oldestToKeep = blockNumber - MAX_VERIFICATION_HISTORY;

      for (const [bn, _] of blockVerifications) {
        if (bn < oldestToKeep) {
          blockVerifications.delete(bn);
          miniblockCounts.delete(bn);
        }
      }
    }
  };

  /**
   * Get verification status for a specific block
   */
  const getBlockVerification = (blockNumber: number): BlockVerification | null => {
    return blockVerifications.get(blockNumber) || null;
  };

  /**
   * Reset rolling window when stale data is detected
   * Called before requesting fresh initial_state from backend
   * Note: Don't reset stats here - handleInitialState will set them from backend
   */
  const resetStaleData = () => {
    // Clear the rolling window - stale data
    rollingWindow.length = 0;

    // Reset first miniblock time so window duration recalculates correctly
    firstMiniblockTime = null;

    // Note: Stats are NOT reset to 0 here - they keep their last values
    // until handleInitialState arrives with fresh data from backend
    // This prevents the "stats go to 0 then jump" issue
  };

  /**
   * Cleanup function - call when component unmounts
   */
  const cleanup = () => {
    clearInterval(syncIntervalId);
    miniblockCounts.clear();
    blockVerifications.clear();
    pauseBuffer.length = 0;
    rollingWindow.length = 0;
  };

  return {
    pendingMiniBlocks,
    handleMiniblock,
    handleMiniblockUpdate,
    handleBlockUpdate,
    handleInitialState,
    resetDataState,
    resetStaleData,
    initializeTimers,
    getNextMiniblock,
    getBlockVerification,
    pause,
    resume,
    setWsClient,
    syncCountdown,
    cleanup,
  };
}
